import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { getHintPatternForWordCells, normalizeWord, removeHyphens } from "../../common/utils/PuzzlePlayLogic";
import { usePagesTranslation } from "../../../locales";
import { ACCENT, SURFACE, TEXT } from "../../../../dark-theme/palette";

const TILE_SX = {
    width: 40,
    height: 44,
    minWidth: 40,
    minHeight: 44,
    borderRadius: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "1.05rem",
    lineHeight: 1,
    userSelect: "none",
    boxSizing: "border-box",
    overflow: "hidden",
};

const SCRAMBLE_TICK_MS = 55;
const SCRAMBLE_BASE_TICKS = 14;
const SCRAMBLE_STAGGER_TICKS = 2;
const DRAG_THRESHOLD_PX = 8;
const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const CORRECT_BORDER = "#F9A825";
const CORRECT_GLOW = "0 0 12px rgba(249, 168, 37, 0.85)";

function lettersFromExpectedWord(expectedWord) {
    return removeHyphens(normalizeWord(expectedWord || ""))
        .toUpperCase()
        .split("")
        .filter((ch) => /[A-Z]/.test(ch));
}

function hashSeed(seed) {
    let h = 2166136261;
    const s = String(seed || "");
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function seededShuffle(letters, seed) {
    const tiles = letters.map((letter, index) => ({
        id: `${seed}-${index}-${letter}`,
        letter,
    }));
    let h = hashSeed(seed);
    for (let i = tiles.length - 1; i > 0; i -= 1) {
        h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
        const j = h % (i + 1);
        [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }
    return tiles;
}

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function scrambleFace(letters, tick, index) {
    if (!letters.length) return "";
    const fromWord = letters[(tick * 3 + index * 5) % letters.length];
    if (tick % 2 === 0) return fromWord;
    return ALPHA[(tick * 11 + index * 17) % ALPHA.length];
}

function buildRack(letters, seed, pattern) {
    const shuffled = seededShuffle(letters, seed);
    const usedIds = new Set();
    const nextSlots = letters.map((_, index) => {
        const lockedLetter = pattern[index] && pattern[index] !== "-" ? pattern[index] : "";
        if (!lockedLetter) {
            return { letter: "", locked: false, tileId: null };
        }
        const tile = shuffled.find((item) => item.letter === lockedLetter && !usedIds.has(item.id));
        if (tile) usedIds.add(tile.id);
        return { letter: lockedLetter, locked: true, tileId: tile?.id || null };
    });
    return {
        shuffled,
        bank: shuffled.map((tile) => ({ ...tile, used: usedIds.has(tile.id) })),
        slots: nextSlots,
    };
}

function pointInRect(x, y, el) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

function shuffleUnusedBank(bank) {
    const next = bank.map((tile) => ({ ...tile }));
    const indexes = [];
    next.forEach((tile, index) => {
        if (!tile.used) indexes.push(index);
    });
    if (indexes.length < 2) return next;
    const original = indexes.map((i) => next[i].id).join("|");
    let h = (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
    for (let i = indexes.length - 1; i > 0; i -= 1) {
        h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
        const j = h % (i + 1);
        const a = indexes[i];
        const b = indexes[j];
        [next[a], next[b]] = [next[b], next[a]];
    }
    if (indexes.map((i) => next[i].id).join("|") === original) {
        const a = indexes[0];
        const b = indexes[1];
        [next[a], next[b]] = [next[b], next[a]];
    }
    return next;
}

/**
 * Two-row letter rack under the crossword grid: scrambled bank on top, empty answer slots below.
 */
export default function CrosswordScrambleRack({
    wordKey = "",
    expectedWord = "",
    cells = [],
    hintRevealCells = [],
    foundCellKeySet,
    meaningPreview = "",
    onLettersChange,
    onCorrectPlacement,
}) {
    const { t } = usePagesTranslation();
    const letters = useMemo(() => lettersFromExpectedWord(expectedWord), [expectedWord]);
    const seed = wordKey || expectedWord || "cross-scramble";
    const pattern = useMemo(
        () => getHintPatternForWordCells(expectedWord, cells, hintRevealCells, foundCellKeySet) || "",
        [expectedWord, cells, hintRevealCells, foundCellKeySet],
    );

    const [bank, setBank] = useState([]);
    const [slots, setSlots] = useState([]);
    const [faces, setFaces] = useState([]);
    const [settled, setSettled] = useState([]);
    const [isScrambling, setIsScrambling] = useState(false);
    const [drag, setDrag] = useState(null);
    const patternRef = useRef(pattern);
    patternRef.current = pattern;
    const bankRef = useRef(bank);
    const slotsRef = useRef(slots);
    bankRef.current = bank;
    slotsRef.current = slots;
    const dragRef = useRef(null);
    const slotElsRef = useRef([]);
    const bankRowRef = useRef(null);
    const lettersRef = useRef(letters);
    lettersRef.current = letters;
    const [correctPop, setCorrectPop] = useState({});
    const scrambleIntervalRef = useRef(null);

    const playScrambleAnimation = useCallback((nextBank) => {
        if (scrambleIntervalRef.current) {
            window.clearInterval(scrambleIntervalRef.current);
            scrambleIntervalRef.current = null;
        }
        const animateIndexes = nextBank
            .map((tile, index) => (tile.used ? -1 : index))
            .filter((index) => index >= 0);
        if (prefersReducedMotion() || animateIndexes.length === 0) {
            setFaces(nextBank.map((tile) => tile.letter));
            setSettled(nextBank.map(() => true));
            setIsScrambling(false);
            return;
        }
        setFaces(nextBank.map((tile) => (tile.used ? tile.letter : "")));
        setSettled(nextBank.map((tile) => Boolean(tile.used)));
        setIsScrambling(true);
        let tick = 0;
        const lastSettleTick =
            SCRAMBLE_BASE_TICKS + Math.max(animateIndexes.length - 1, 0) * SCRAMBLE_STAGGER_TICKS;
        scrambleIntervalRef.current = window.setInterval(() => {
            tick += 1;
            setFaces(
                nextBank.map((tile, index) => {
                    if (tile.used) return tile.letter;
                    const order = animateIndexes.indexOf(index);
                    const settleAt = SCRAMBLE_BASE_TICKS + order * SCRAMBLE_STAGGER_TICKS;
                    if (tick >= settleAt) return tile.letter;
                    return scrambleFace(lettersRef.current, tick, index);
                }),
            );
            setSettled(
                nextBank.map((tile, index) => {
                    if (tile.used) return true;
                    const order = animateIndexes.indexOf(index);
                    return tick >= SCRAMBLE_BASE_TICKS + order * SCRAMBLE_STAGGER_TICKS;
                }),
            );
            if (tick >= lastSettleTick) {
                window.clearInterval(scrambleIntervalRef.current);
                scrambleIntervalRef.current = null;
                setFaces(nextBank.map((tile) => tile.letter));
                setSettled(nextBank.map(() => true));
                setIsScrambling(false);
            }
        }, SCRAMBLE_TICK_MS);
    }, []);

    useEffect(() => {
        if (letters.length === 0) {
            setBank([]);
            setSlots([]);
            setFaces([]);
            setSettled([]);
            setIsScrambling(false);
            setDrag(null);
            setCorrectPop({});
            dragRef.current = null;
            if (scrambleIntervalRef.current) {
                window.clearInterval(scrambleIntervalRef.current);
                scrambleIntervalRef.current = null;
            }
            return undefined;
        }
        const { bank: nextBank, slots: nextSlots } = buildRack(letters, seed, patternRef.current);
        setBank(nextBank);
        setSlots(nextSlots);
        playScrambleAnimation(nextBank);
        return () => {
            if (scrambleIntervalRef.current) {
                window.clearInterval(scrambleIntervalRef.current);
                scrambleIntervalRef.current = null;
            }
        };
    }, [letters, seed, playScrambleAnimation]);

    useEffect(() => {
        if (letters.length === 0) return;
        const { bank: nextBank, slots: nextSlots } = buildRack(letters, seed, pattern);
        setBank(nextBank);
        setSlots(nextSlots);
    }, [letters, seed, pattern]);

    const emitLetters = useCallback(
        (nextSlots) => {
            const spelled = nextSlots.map((slot) => slot.letter || "").join("");
            onLettersChange?.(spelled);
        },
        [onLettersChange],
    );

    const commitRack = useCallback(
        (nextBank, nextSlots) => {
            const prevSlots = slotsRef.current;
            setBank(nextBank);
            setSlots(nextSlots);
            emitLetters(nextSlots);
            const expected = lettersRef.current;
            const newlyCorrect = [];
            nextSlots.forEach((slot, index) => {
                if (!slot?.letter || slot.letter !== expected[index]) return;
                const prev = prevSlots[index];
                if (prev && prev.letter === slot.letter && prev.tileId === slot.tileId) return;
                newlyCorrect.push(index);
            });
            if (newlyCorrect.length === 0) return;
            setCorrectPop((prev) => {
                const next = { ...prev };
                newlyCorrect.forEach((index) => {
                    next[index] = (next[index] || 0) + 1;
                });
                return next;
            });
            onCorrectPlacement?.(newlyCorrect);
        },
        [emitLetters, onCorrectPlacement],
    );

    const placeBankTileInSlot = useCallback(
        (tileId, slotIndex) => {
            const currentBank = bankRef.current;
            const currentSlots = slotsRef.current;
            const tile = currentBank.find((item) => item.id === tileId);
            const target = currentSlots[slotIndex];
            if (!tile || tile.used || !target || target.locked) return;
            let nextBank = currentBank;
            if (target.tileId && target.tileId !== tileId) {
                nextBank = nextBank.map((item) =>
                    item.id === target.tileId ? { ...item, used: false } : item,
                );
            }
            nextBank = nextBank.map((item) => (item.id === tileId ? { ...item, used: true } : item));
            const nextSlots = currentSlots.map((slot, index) =>
                index === slotIndex ? { ...slot, letter: tile.letter, tileId: tile.id } : slot,
            );
            commitRack(nextBank, nextSlots);
        },
        [commitRack],
    );

    const returnSlotToBank = useCallback(
        (slotIndex) => {
            const currentBank = bankRef.current;
            const currentSlots = slotsRef.current;
            const slot = currentSlots[slotIndex];
            if (!slot || slot.locked || !slot.letter) return;
            const nextSlots = currentSlots.map((item, index) =>
                index === slotIndex ? { ...item, letter: "", tileId: null } : item,
            );
            const nextBank = currentBank.map((item) =>
                item.id === slot.tileId ? { ...item, used: false } : item,
            );
            commitRack(nextBank, nextSlots);
        },
        [commitRack],
    );

    const moveSlotToSlot = useCallback(
        (fromIndex, toIndex) => {
            if (fromIndex === toIndex) return;
            const currentSlots = slotsRef.current;
            const from = currentSlots[fromIndex];
            const to = currentSlots[toIndex];
            if (!from || !to || from.locked || to.locked || !from.letter) return;
            const nextSlots = currentSlots.map((slot, index) => {
                if (index === toIndex) return { ...slot, letter: from.letter, tileId: from.tileId };
                if (index === fromIndex) return { ...slot, letter: to.letter || "", tileId: to.tileId || null };
                return slot;
            });
            commitRack(bankRef.current, nextSlots);
        },
        [commitRack],
    );

    const handleBankClick = (tileId) => {
        if (isScrambling || dragRef.current?.moved) return;
        const emptyIndex = slotsRef.current.findIndex((slot) => !slot.locked && !slot.letter);
        if (emptyIndex < 0) return;
        placeBankTileInSlot(tileId, emptyIndex);
    };

    const handleSlotClick = (slotIndex) => {
        if (isScrambling || dragRef.current?.moved) return;
        returnSlotToBank(slotIndex);
    };

    const resolveDrop = useCallback((x, y) => {
        const slotIndex = slotElsRef.current.findIndex((el) => pointInRect(x, y, el));
        if (slotIndex >= 0) return { type: "slot", index: slotIndex };
        if (pointInRect(x, y, bankRowRef.current)) return { type: "bank" };
        return { type: "none" };
    }, []);

    const updateDragFromPointer = useCallback(
        (event, extras = {}) => {
            const next = {
                ...(dragRef.current || {}),
                ...extras,
                x: event.clientX,
                y: event.clientY,
            };
            const drop = resolveDrop(event.clientX, event.clientY);
            next.overSlot = drop.type === "slot" ? drop.index : null;
            next.overBank = drop.type === "bank";
            dragRef.current = next;
            setDrag(next);
        },
        [resolveDrop],
    );

    const handlePointerDown = (event, payload) => {
        if (isScrambling || event.button === 2) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const rect = event.currentTarget.getBoundingClientRect();
        const next = {
            ...payload,
            pointerId: event.pointerId,
            originX: event.clientX,
            originY: event.clientY,
            x: event.clientX,
            y: event.clientY,
            grabX: event.clientX - rect.left,
            grabY: event.clientY - rect.top,
            moved: false,
            overSlot: null,
            overBank: false,
        };
        dragRef.current = next;
        setDrag(next);
    };

    const handlePointerMove = (event) => {
        const current = dragRef.current;
        if (!current || current.pointerId !== event.pointerId) return;
        const dx = event.clientX - current.originX;
        const dy = event.clientY - current.originY;
        const moved = current.moved || Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX;
        updateDragFromPointer(event, { moved });
    };

    const finishDrag = (event) => {
        const current = dragRef.current;
        if (!current || current.pointerId !== event.pointerId) return;
        const drop = resolveDrop(event.clientX, event.clientY);
        const didMove = current.moved;
        dragRef.current = { ...current, moved: didMove };
        if (didMove) {
            if (current.from === "bank") {
                if (drop.type === "slot") placeBankTileInSlot(current.tileId, drop.index);
            } else if (current.from === "slot") {
                if (drop.type === "slot") moveSlotToSlot(current.fromIndex, drop.index);
                else if (drop.type === "bank") returnSlotToBank(current.fromIndex);
            }
        } else if (current.from === "bank") {
            handleBankClick(current.tileId);
        } else if (current.from === "slot") {
            handleSlotClick(current.fromIndex);
        }
        dragRef.current = null;
        setDrag(null);
    };

    const hasWord = letters.length > 0;
    const meaningText = String(meaningPreview || "").trim();
    const dragging = Boolean(drag?.moved);
    const unusedCount = bank.filter((tile) => !tile.used).length;
    const canShuffle = hasWord && unusedCount >= 2 && !isScrambling && !dragging;
    const canReset = hasWord && slots.some((slot) => !slot.locked && Boolean(slot.letter)) && !isScrambling && !dragging;

    const handleShuffle = () => {
        if (!canShuffle) return;
        const nextBank = shuffleUnusedBank(bankRef.current);
        setBank(nextBank);
        playScrambleAnimation(nextBank);
    };

    const handleResetRow = () => {
        if (!canReset) return;
        const returnedIds = new Set();
        const nextSlots = slotsRef.current.map((slot) => {
            if (slot.locked || !slot.letter) return slot;
            if (slot.tileId) returnedIds.add(slot.tileId);
            return { ...slot, letter: "", tileId: null };
        });
        const nextBank = bankRef.current.map((item) =>
            returnedIds.has(item.id) ? { ...item, used: false } : item,
        );
        setCorrectPop({});
        commitRack(nextBank, nextSlots);
    };

    const typedPrefix = useMemo(() => {
        let spelled = "";
        for (let i = 0; i < slots.length; i += 1) {
            if (!slots[i]?.letter) break;
            spelled += slots[i].letter;
        }
        return spelled;
    }, [slots]);

    const placeTypedLetters = useCallback(
        (added) => {
            const chars = String(added || "").toUpperCase().replace(/[^A-Z]/g, "");
            if (!chars || isScrambling) return;
            let nextBank = bankRef.current.map((item) => ({ ...item }));
            let nextSlots = slotsRef.current.map((slot) => ({ ...slot }));
            let placed = false;
            for (const ch of chars) {
                const emptyIndex = nextSlots.findIndex((slot) => !slot.locked && !slot.letter);
                const tile = nextBank.find((item) => !item.used && item.letter === ch);
                if (emptyIndex < 0 || !tile) break;
                const target = nextSlots[emptyIndex];
                if (target.tileId && target.tileId !== tile.id) {
                    nextBank = nextBank.map((item) =>
                        item.id === target.tileId ? { ...item, used: false } : item,
                    );
                }
                nextBank = nextBank.map((item) => (item.id === tile.id ? { ...item, used: true } : item));
                nextSlots = nextSlots.map((slot, index) =>
                    index === emptyIndex ? { ...slot, letter: tile.letter, tileId: tile.id } : slot,
                );
                placed = true;
            }
            if (placed) commitRack(nextBank, nextSlots);
        },
        [commitRack, isScrambling],
    );

    const removeLastTypedLetter = useCallback(() => {
        if (isScrambling) return;
        const currentSlots = slotsRef.current;
        let end = 0;
        while (end < currentSlots.length && currentSlots[end]?.letter) end += 1;
        for (let i = end - 1; i >= 0; i -= 1) {
            if (currentSlots[i].locked) continue;
            returnSlotToBank(i);
            return;
        }
    }, [isScrambling, returnSlotToBank]);

    useEffect(() => {
        if (!hasWord) return undefined;
        const onKeyDown = (event) => {
            if (event.ctrlKey || event.metaKey || event.altKey || isScrambling) return;
            const el = document.activeElement;
            const tag = el?.tagName?.toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable) {
                return;
            }
            if (event.key === "Backspace") {
                event.preventDefault();
                event.stopPropagation();
                removeLastTypedLetter();
                return;
            }
            if (event.key.length !== 1 || !/^[a-zA-Z]$/.test(event.key)) return;
            event.preventDefault();
            event.stopPropagation();
            placeTypedLetters(event.key);
        };
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [hasWord, isScrambling, placeTypedLetters, removeLastTypedLetter]);

    return (
        <Box
            sx={{
                mt: 1.5,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                p: 1.25,
                backgroundColor: SURFACE.card,
                color: TEXT.primary,
                borderRadius: 1,
                border: `1px solid ${SURFACE.border}`,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                position: "relative",
            }}
        >
            {hasWord ? (
                <>
                <Typography
                    variant="caption"
                    sx={{
                        color: TEXT.secondary,
                        textAlign: "center",
                        display: "block",
                        lineHeight: 1.35,
                        px: 0.5,
                    }}
                >
                    {t(
                        "puzzle.playCross.scramble-place-hint",
                        "Place the scrambled letters in the right place.",
                    )}
                </Typography>
                <TextField
                    size="small"
                    fullWidth
                    label={t("puzzle.playCross.scramble-type-word", "Type the word")}
                    value={typedPrefix}
                    disabled={isScrambling}
                    autoComplete="off"
                    onChange={() => {}}
                    onKeyDown={(event) => {
                        if (event.ctrlKey || event.metaKey || event.altKey || isScrambling) return;
                        if (event.key === "Backspace") {
                            event.preventDefault();
                            removeLastTypedLetter();
                            return;
                        }
                        if (event.key.length === 1 && /^[a-zA-Z]$/.test(event.key)) {
                            event.preventDefault();
                            placeTypedLetters(event.key);
                        }
                    }}
                    onPaste={(event) => {
                        event.preventDefault();
                        placeTypedLetters(event.clipboardData?.getData("text") || "");
                    }}
                    slotProps={{
                        htmlInput: {
                            autoCorrect: "off",
                            spellCheck: false,
                            "aria-label": t("puzzle.playCross.scramble-type-word", "Type the word"),
                        },
                    }}
                    sx={{
                        maxWidth: 420,
                        alignSelf: "center",
                        "& .MuiInputBase-root": {
                            color: TEXT.primary,
                            backgroundColor: SURFACE.sunken,
                        },
                        "& .MuiInputLabel-root": { color: TEXT.secondary },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: SURFACE.border },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: SURFACE.borderStrong,
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: ACCENT.light,
                        },
                    }}
                />
                </>
            ) : (
                <Typography variant="body2" sx={{ color: TEXT.secondary, textAlign: "center" }}>
                    {t("puzzle.playCross.scramble-select-hint", "Select a word on the grid to fill letters.")}
                </Typography>
            )}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: hasWord
                        ? `repeat(${letters.length}, ${TILE_SX.width}px) 44px`
                        : "1fr",
                    justifyContent: "center",
                    justifyItems: "center",
                    alignItems: "center",
                    columnGap: 0.75,
                    rowGap: 1,
                    width: "100%",
                    overflow: "hidden",
                }}
            >
                {hasWord ? (
                    <Box
                        ref={bankRowRef}
                        aria-hidden
                        sx={{
                            gridColumn: `1 / span ${letters.length}`,
                            gridRow: 1,
                            width: "100%",
                            height: "100%",
                            minHeight: 44,
                            borderRadius: 1,
                            pointerEvents: "none",
                            outline: drag?.moved && drag?.overBank ? `2px dashed ${ACCENT.main}` : "none",
                            outlineOffset: 4,
                        }}
                    />
                ) : null}
                <Box
                    role="group"
                    aria-label={t("puzzle.playCross.scramble-bank-a11y", "Scrambled letters")}
                    aria-busy={isScrambling}
                    sx={{ display: "contents" }}
                >
                {hasWord
                    ? bank.map((tile, index) => {
                          const displayLetter = faces[index] || tile.letter;
                          const tileSettled = Boolean(settled[index]);
                          const isLifted = dragging && drag.from === "bank" && drag.tileId === tile.id;
                          return (
                              <Box
                                  key={tile.id}
                                  component="button"
                                  type="button"
                                  disabled={tile.used || isScrambling}
                                  aria-label={t("puzzle.playCross.scramble-drag-letter", "Drag letter {{letter}}", {
                                      letter: tile.letter,
                                  })}
                                  onPointerDown={(event) => {
                                      if (tile.used || isScrambling) return;
                                      handlePointerDown(event, {
                                          from: "bank",
                                          tileId: tile.id,
                                          letter: tile.letter,
                                      });
                                  }}
                                  onPointerMove={handlePointerMove}
                                  onPointerUp={finishDrag}
                                  onPointerCancel={finishDrag}
                                  sx={{
                                      ...TILE_SX,
                                      gridColumn: index + 1,
                                      gridRow: 1,
                                      touchAction: "none",
                                      border: `1px solid ${tile.used ? SURFACE.border : ACCENT.main}`,
                                      backgroundColor: tile.used ? SURFACE.sunken : ACCENT.soft,
                                      color: tile.used ? TEXT.disabled : TEXT.primary,
                                      cursor: tile.used || isScrambling ? "default" : dragging ? "grabbing" : "grab",
                                      visibility: tile.used ? "hidden" : "visible",
                                      opacity: isLifted ? 0.35 : 1,
                                      pointerEvents: tile.used || isScrambling ? "none" : "auto",
                                      animation:
                                          isScrambling && !tileSettled
                                              ? "scrambleBoxShake 120ms ease-in-out infinite"
                                              : tileSettled && isScrambling
                                                ? "scrambleBoxLand 320ms cubic-bezier(0.22, 1, 0.36, 1)"
                                                : "none",
                                      "@keyframes scrambleBoxShake": {
                                          "0%": { transform: "translateY(0) rotate(0deg)" },
                                          "25%": { transform: "translateY(-2px) rotate(-4deg)" },
                                          "50%": { transform: "translateY(2px) rotate(3deg)" },
                                          "75%": { transform: "translateY(-1px) rotate(-2deg)" },
                                          "100%": { transform: "translateY(0) rotate(0deg)" },
                                      },
                                      "@keyframes scrambleBoxLand": {
                                          "0%": { transform: "scale(1.12)" },
                                          "55%": { transform: "scale(0.94)" },
                                          "100%": { transform: "scale(1)" },
                                      },
                                      "&:hover": tile.used || isScrambling
                                          ? undefined
                                          : { backgroundColor: SURFACE.hover, borderColor: ACCENT.light },
                                  }}
                              >
                                  <Box
                                      component="span"
                                      key={`${tile.id}-${displayLetter}-${tileSettled ? "in" : "spin"}`}
                                      sx={{
                                          display: "block",
                                          pointerEvents: "none",
                                          animation: !tileSettled
                                              ? "scrambleReel 48ms linear"
                                              : "scrambleLetterLand 320ms cubic-bezier(0.22, 1, 0.36, 1)",
                                          "@keyframes scrambleReel": {
                                              "0%": {
                                                  transform: "translateY(-120%) scale(0.86)",
                                                  opacity: 0.2,
                                                  filter: "blur(3px)",
                                              },
                                              "55%": {
                                                  transform: "translateY(18%) scale(1.08)",
                                                  opacity: 0.85,
                                                  filter: "blur(1px)",
                                              },
                                              "100%": {
                                                  transform: "translateY(0) scale(1)",
                                                  opacity: 1,
                                                  filter: "blur(0)",
                                              },
                                          },
                                          "@keyframes scrambleLetterLand": {
                                              "0%": { transform: "scale(1.2) rotate(-8deg)" },
                                              "100%": { transform: "scale(1) rotate(0deg)" },
                                          },
                                      }}
                                  >
                                      {displayLetter}
                                  </Box>
                              </Box>
                          );
                      })
                    : null}
                </Box>
                {hasWord ? (
                    <IconButton
                        type="button"
                        onClick={handleShuffle}
                        disabled={!canShuffle}
                        aria-label={t("puzzle.playCross.scramble-shuffle", "Shuffle letters")}
                        sx={{
                            width: 44,
                            height: 44,
                            minWidth: 44,
                            minHeight: 44,
                            gridColumn: letters.length + 1,
                            gridRow: 1,
                            color: ACCENT.light,
                            "&:hover": { backgroundColor: SURFACE.hover, color: ACCENT.light },
                            "&.Mui-disabled": { color: TEXT.disabled },
                        }}
                    >
                        <ShuffleIcon />
                    </IconButton>
                ) : null}
                <Box
                    role="group"
                    aria-label={t("puzzle.playCross.scramble-slots-a11y", "Answer letters")}
                    sx={{ display: "contents" }}
                >
                {hasWord
                    ? slots.map((slot, index) => {
                          const isDropTarget = dragging && drag.overSlot === index && !slot.locked;
                          const isLifted = dragging && drag.from === "slot" && drag.fromIndex === index;
                          const canDrag = Boolean(slot.letter) && !slot.locked && !isScrambling;
                          const isCorrect = Boolean(slot.letter) && slot.letter === letters[index];
                          const popToken = correctPop[index] || 0;
                          return (
                              <Box
                                  key={`slot-${index}`}
                                  ref={(el) => {
                                      slotElsRef.current[index] = el;
                                  }}
                                  component={canDrag ? "button" : "div"}
                                  type={canDrag ? "button" : undefined}
                                  aria-label={
                                      slot.letter
                                          ? t("puzzle.playCross.scramble-drag-letter", "Drag letter {{letter}}", {
                                                letter: slot.letter,
                                            })
                                          : t("puzzle.playCross.scramble-drop-slot", "Drop letter here")
                                  }
                                  onPointerDown={
                                      canDrag
                                          ? (event) =>
                                                handlePointerDown(event, {
                                                    from: "slot",
                                                    fromIndex: index,
                                                    tileId: slot.tileId,
                                                    letter: slot.letter,
                                                })
                                          : undefined
                                  }
                                  onPointerMove={canDrag ? handlePointerMove : undefined}
                                  onPointerUp={canDrag ? finishDrag : undefined}
                                  onPointerCancel={canDrag ? finishDrag : undefined}
                                  sx={{
                                      ...TILE_SX,
                                      gridColumn: index + 1,
                                      gridRow: 2,
                                      overflow: isCorrect || popToken ? "visible" : "hidden",
                                      touchAction: canDrag ? "none" : "auto",
                                      border: isDropTarget
                                          ? `2px solid ${ACCENT.light}`
                                          : isCorrect
                                            ? `2px solid ${CORRECT_BORDER}`
                                            : slot.letter
                                              ? `2px solid ${ACCENT.main}`
                                              : `2px dashed ${SURFACE.borderStrong}`,
                                      backgroundColor: isDropTarget
                                          ? SURFACE.hover
                                          : isCorrect
                                            ? "rgba(249, 168, 37, 0.18)"
                                            : slot.locked
                                              ? ACCENT.soft
                                              : SURFACE.sunken,
                                      color: TEXT.primary,
                                      cursor: canDrag ? (dragging ? "grabbing" : "grab") : dragging ? "copy" : "default",
                                      ...(isDropTarget && !popToken ? { transform: "scale(1.08)" } : {}),
                                      opacity: isLifted ? 0.35 : 1,
                                      boxShadow: isDropTarget
                                          ? `0 0 0 2px ${ACCENT.main}`
                                          : isCorrect
                                            ? CORRECT_GLOW
                                            : "none",
                                      animation: popToken ? "correctLockIn 560ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
                                      "@keyframes correctLockIn": {
                                          "0%": { transform: "scale(1)", boxShadow: "0 0 0 rgba(249, 168, 37, 0)" },
                                          "28%": {
                                              transform: "scale(1.2)",
                                              boxShadow: "0 0 20px 4px rgba(249, 168, 37, 0.95)",
                                          },
                                          "62%": { transform: "scale(0.94)" },
                                          "100%": {
                                              transform: "scale(1)",
                                              boxShadow: CORRECT_GLOW,
                                          },
                                      },
                                      transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                                  }}
                              >
                                  {isLifted ? (
                                      ""
                                  ) : (
                                      <Box
                                          component="span"
                                          key={`slot-letter-${index}-${popToken}-${slot.letter || ""}`}
                                          sx={{
                                              display: "block",
                                              animation: popToken
                                                  ? "correctLetterPop 480ms cubic-bezier(0.22, 1, 0.36, 1)"
                                                  : "none",
                                              "@keyframes correctLetterPop": {
                                                  "0%": { transform: "scale(1) rotate(0deg)" },
                                                  "35%": { transform: "scale(1.34) rotate(-8deg)" },
                                                  "65%": { transform: "scale(0.92) rotate(4deg)" },
                                                  "100%": { transform: "scale(1) rotate(0deg)" },
                                              },
                                          }}
                                      >
                                          {slot.letter || ""}
                                      </Box>
                                  )}
                              </Box>
                          );
                      })
                    : null}
                </Box>
                {hasWord ? (
                    <IconButton
                        type="button"
                        onClick={handleResetRow}
                        disabled={!canReset}
                        aria-label={t("puzzle.playCross.scramble-reset", "Reset letters")}
                        sx={{
                            width: 44,
                            height: 44,
                            minWidth: 44,
                            minHeight: 44,
                            gridColumn: letters.length + 1,
                            gridRow: 2,
                            color: ACCENT.light,
                            "&:hover": { backgroundColor: SURFACE.hover, color: ACCENT.light },
                            "&.Mui-disabled": { color: TEXT.disabled },
                        }}
                    >
                        <RestartAltIcon />
                    </IconButton>
                ) : null}
            </Box>
            {hasWord ? (
                <Typography
                    variant="body2"
                    sx={{
                        color: meaningText ? TEXT.primary : TEXT.disabled,
                        textAlign: "center",
                        fontWeight: 800,
                        lineHeight: 1.45,
                        px: 0.5,
                    }}
                >
                    {meaningText ||
                        t("puzzle.playCross.word-wheel-no-meaning", "No clue meaning for this word.")}
                </Typography>
            ) : null}
            {dragging && typeof document !== "undefined"
                ? createPortal(
                      <Box
                          aria-hidden
                          sx={{
                              position: "fixed",
                              inset: 0,
                              overflow: "hidden",
                              pointerEvents: "none",
                              zIndex: 4000,
                          }}
                      >
                          <Box
                              sx={{
                                  ...TILE_SX,
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  margin: 0,
                                  transform: `translate3d(${drag.x - (drag.grabX ?? TILE_SX.width / 2)}px, ${drag.y - (drag.grabY ?? TILE_SX.height / 2)}px, 0)`,
                                  border: `1px solid ${ACCENT.light}`,
                                  backgroundColor: ACCENT.soft,
                                  color: TEXT.primary,
                                  boxShadow: "0 10px 24px rgba(2, 6, 18, 0.45)",
                              }}
                          >
                              {drag.letter}
                          </Box>
                      </Box>,
                      document.body,
                  )
                : null}
        </Box>
    );
}
